import React from "react";
import ReactDOM from "react-dom/client";
// Init Sentry le plus tot possible (l'import execute config/sentry.js -> Sentry.init).
import { captureToSentry } from "./config/sentry";
import "./index.css"; // Optional if you have some styles to include
import App from "./config/App";

import { SnackbarProvider } from "notistack";
import { BrowserRouter as Router } from "react-router-dom";

// Please make sure to call this method only once!

// Import Material UI components
import { lightTheme, darkTheme } from "./config/theme";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";

import { NotificationStyle } from "./notification/NotificationStyle";

import { AppProvider } from "./config/AppContext";
import ErrorBoundary from "./components/ErrorBoundary";
import sendErrorDiscord from "./api/sendErrorDiscord";
import { getAuthCentral } from "./config/FirebaseConfig";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { checkDB } from "./models/DataFunctions";

// Apply globally
import { ConfigProvider } from "antd";
import frFR from "antd/locale/fr_FR"; // Locale for French
import moment from "moment";
import "moment/locale/fr";
import dayjs from "dayjs";

import "dayjs/locale/fr";

dayjs.locale("fr_FR");

moment.locale("fr");

const originalConsoleError = console.error;

console.error = (...args) => {
  if (
    args[0]?.includes &&
    args[0].includes("MUI X: useResizeContainer - The parent DOM element")
  ) {
    return; // Suppress this specific warning
  }
  originalConsoleError(...args); // Log other errors as usual
};

// Gestionnaire global d'erreurs JavaScript non capturées
const prefix = "https://api.hexagone.expert/";

// Helper function to get user context from Firebase Auth and Firestore
const getUserContext = async () => {
  try {
    const auth = getAuthCentral();
    const currentUser = auth.currentUser;

    if (currentUser) {
      const db = getFirestore();
      const userRef = doc(db, "user", currentUser.uid);
      const userSnapshot = await getDoc(checkDB(userRef));

      if (userSnapshot.exists()) {
        return {
          email: currentUser.email || userSnapshot.get("email"),
          uid: currentUser.uid,
          org_id: userSnapshot.get("org_id"),
        };
      }
    }
  } catch (e) {
    console.error("Failed to get user context:", e);
  }
  return null;
};

// Helper function to format error messages
const formatErrorMessage = async (errorData, errorType) => {
  let message = `⚠️ **${errorType}**\n\n`;

  const userContext = await getUserContext();
  if (userContext) {
    message += "👤 **User Information:**\n";
    message += `• Email: ${userContext.email || "N/A"}\n`;
    message += `• User ID: ${userContext.uid || "N/A"}\n`;
    message += `• Organization ID: ${userContext.org_id || "N/A"}\n\n`;
  }

  message += "⚠️ **Error Details:**\n";
  message += `• Message: ${errorData.message || errorData.reason || "N/A"}\n`;
  if (errorData.errorName) {
    message += `• Error Type: ${errorData.errorName}\n`;
  }
  message += `• Timestamp: ${errorData.timestamp || "N/A"}\n`;
  message += `• URL: ${errorData.url || "N/A"}\n`;

  if (errorData.pathname) {
    message += `• Route: ${errorData.pathname}${errorData.search || ""}${errorData.hash || ""}\n`;
  }

  if (errorData.source) {
    message += `• Source File: ${errorData.source}\n`;
    message += `• Location: Line ${errorData.line}, Column ${errorData.column}\n`;
  }

  message += `• User Agent: ${errorData.userAgent || "N/A"}\n\n`;

  if (errorData.stack) {
    message += "📋 **Stack Trace:**\n```\n";
    message += errorData.stack.substring(0, 1500);
    message += "\n```\n\n";
  }

  // Add helpful debugging hint
  message += "💡 **Debugging Hint:**\n";
  message += "Search for the error message in your codebase to find the source.\n";
  message += "Check the Component Stack in browser console for React components.\n";

  return message;
};

window.onerror = (message, source, lineno, colno, error) => {
  const errorMessage = {
    message: message,
    source: source,
    line: lineno,
    column: colno,
    stack: error?.stack,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    // Additional context
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    errorName: error?.name,
    errorMessage: error?.message,
  };

  console.error("Global error handler:", errorMessage);
  console.error("Full error object:", error);
  console.error("Stack trace:", error?.stack);

  // Sentry : on capture l'objet Error si dispo (stack propre), sinon le message.
  captureToSentry(error || message, {
    tags: { handler: "window.onerror" },
    extra: errorMessage,
  });

  // Format and send error asynchronously
  formatErrorMessage(errorMessage, "JAVASCRIPT ERROR").then((formattedMessage) => {
    sendErrorDiscord(formattedMessage, prefix);
  });

  return false; // Permet aux autres gestionnaires de recevoir l'erreur
};

// Gestionnaire pour les promesses rejetées non gérées
window.onunhandledrejection = (event) => {
  const errorMessage = {
    type: "Unhandled Promise Rejection",
    reason: event.reason?.toString(),
    stack: event.reason?.stack,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
  };

  console.error("Unhandled promise rejection:", errorMessage);

  // Sentry : capture la raison du rejet (Error si possible).
  captureToSentry(event.reason || "Unhandled promise rejection", {
    tags: { handler: "window.onunhandledrejection" },
    extra: errorMessage,
  });

  // Format and send error asynchronously
  formatErrorMessage(errorMessage, "UNHANDLED PROMISE REJECTION").then((formattedMessage) => {
    sendErrorDiscord(formattedMessage, prefix);
  });
};

const root = ReactDOM.createRoot(document.getElementById("root"));
// Wrap the application with ThemeProvider

root.render(
  <ErrorBoundary prefix={prefix}>
    <ConfigProvider locale={frFR}>
      <ThemeProvider theme={lightTheme}>
        {/* CssBaseline resets default browser styles for consistency */}
        <CssBaseline />
        <SnackbarProvider
          maxSnack={8} // Wrap App with SnackbarProvider
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }} // Customize position
          autoHideDuration={2000}
          Components={{
            success: NotificationStyle,
            error: NotificationStyle,
            info: NotificationStyle,
          }}
        >
          <AppProvider>
            <Router>
              <App />
            </Router>
          </AppProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </ConfigProvider>
  </ErrorBoundary>
);
